import { useState } from "react";

import { FolderOpen, Save, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  type RunPreset,
  SOURCE_LABEL,
} from "@/pages/tina/runs/components/site-picker/utils";

interface RunPresetsBarProps {
  presets: RunPreset[];
  canSave: boolean;
  onSave: (name: string) => void;
  onLoad: (preset: RunPreset) => void;
  onDelete: (id: string) => void;
}

export function RunPresetsBar({
  presets,
  canSave,
  onSave,
  onLoad,
  onDelete,
}: RunPresetsBarProps) {
  const [name, setName] = useState("");

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed || !canSave) return;
    onSave(trimmed);
    setName("");
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex items-center gap-2 border-b p-3">
        <h3 className="text-sm font-semibold">Presets</h3>
        <span className="text-xs text-muted-foreground">
          Saved per content type
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3 p-3">
        <div className="flex gap-2">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Preset name"
            className="h-9 w-48"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSave();
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!name.trim() || !canSave}
            onClick={handleSave}
          >
            <Save className="mr-1 h-4 w-4" />
            Save
          </Button>
        </div>

        {presets.length === 0 ? (
          <span className="text-sm text-muted-foreground">
            No presets saved for this content type yet.
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {presets.map((preset) => (
              <div
                key={preset.id}
                className="flex items-center gap-2 rounded-md border bg-background p-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{preset.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {preset.sites.length} sites · {SOURCE_LABEL[preset.source]}{" "}
                    · {preset.country}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Load preset ${preset.name}`}
                  onClick={() => onLoad(preset)}
                >
                  <FolderOpen className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label={`Delete preset ${preset.name}`}
                  onClick={() => onDelete(preset.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
